import { useEffect, useState } from 'react';
import { Icon, Input, Toggle } from '@ab-inbev-maz/compass';
import type { IconName } from '@ab-inbev-maz/compass/dist/entities/components/icon.js';
import { useCanvasEditor } from './CanvasEditorContext';
import {
  ANIMATION_TYPES,
  DEFAULT_ANIMATION_CONFIG,
  playObjectAnimation,
  type AnimatableObject,
  type AnimationTypeId,
} from './CanvasAnimations';

const DURATION_PRESETS = [1.5, 0.8, 0.3];

const ANIMATION_ICON_NAMES: Record<AnimationTypeId, IconName> = {
  'fade-in': 'system/star-unfilled',
  slide: 'arrow/chevron-double-right',
  zoom: 'system/zoom-in',
  bounce: 'arrow/upload',
  rotate: 'arrow/rotate-cw',
  dissolve: 'system/eye-on',
};

const AnimationIcon = ({ type }: { type: AnimationTypeId }) => (
  <span className="text-lg text-gray-700">
    <Icon name={ANIMATION_ICON_NAMES[type]} />
  </span>
);


// "Animation" tab panel: configures type/duration/loop/repeat count/delay
// of the selected element (image or text), stored in its animationConfig.
const CanvasAnimationTab = () => {
  const { canvas, selectedObject, selectedObjectVersion, applyAnimation, removeAnimation } = useCanvasEditor();
  const config = (selectedObject as AnimatableObject | null)?.animationConfig;

  const [customDuration, setCustomDuration] = useState(String(config?.duration ?? DEFAULT_ANIMATION_CONFIG.duration));
  const [customRepeatCount, setCustomRepeatCount] = useState(
    String(config?.repeatCount ?? DEFAULT_ANIMATION_CONFIG.repeatCount)
  );
  const [customDelay, setCustomDelay] = useState(String(config?.delay ?? DEFAULT_ANIMATION_CONFIG.delay));

  // Reloads every field when another element is selected.
  useEffect(() => {
    setCustomDuration(String(config?.duration ?? DEFAULT_ANIMATION_CONFIG.duration));
    setCustomRepeatCount(String(config?.repeatCount ?? DEFAULT_ANIMATION_CONFIG.repeatCount));
    setCustomDelay(String(config?.delay ?? DEFAULT_ANIMATION_CONFIG.delay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObject]);

  // Resyncs the fields when the animation is edited elsewhere. A draft that already parses to the stored
  // value is kept as typed, otherwise applying "1." (stored as 1) would erase the dot and block decimals.
  useEffect(() => {
    const keepDraft = (parse: (value: string) => number, next: number) => (draft: string) =>
      parse(draft) === next ? draft : String(next);
    setCustomDuration(keepDraft(Number.parseFloat, config?.duration ?? DEFAULT_ANIMATION_CONFIG.duration));
    setCustomRepeatCount(keepDraft((v) => Number.parseInt(v, 10), config?.repeatCount ?? DEFAULT_ANIMATION_CONFIG.repeatCount));
    setCustomDelay(keepDraft(Number.parseFloat, config?.delay ?? DEFAULT_ANIMATION_CONFIG.delay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObjectVersion]);

  const handleDurationChange = (value: string) => {
    setCustomDuration(value);
    const duration = Number.parseFloat(value);
    if (duration > 0) {
      applyAnimation({ duration });
      previewAnimation();
    }
  };

  const handleRepeatCountChange = (value: string) => {
    setCustomRepeatCount(value);
    const repeatCount = Number.parseInt(value, 10);
    if (repeatCount > 0) {
      applyAnimation({ repeatCount });
      previewAnimation();
    }
  };

  const handleDelayChange = (value: string) => {
    setCustomDelay(value);
    const delay = Number.parseFloat(value);
    if (delay >= 0) {
      applyAnimation({ delay });
      previewAnimation();
    }
  };

  const handleLoopInfiniteChange = (loopInfinite: boolean) => {
    applyAnimation({ loopInfinite });
    previewAnimation();
  };

  // Plays the element with its current animation as a preview.
  const previewAnimation = () => {
    const activeObject = selectedObject as AnimatableObject | null;
    if (canvas && activeObject?.animationConfig?.type) playObjectAnimation(canvas, activeObject);
  };

  const handleSelectAnimation = (type: AnimationTypeId) => {
    applyAnimation({ type });
    previewAnimation();
  };

  if (!selectedObject) {
    return <p className="text-sm text-gray-500">Selecciona un elemento del canvas para animarlo.</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3">
        {ANIMATION_TYPES.map((animation) => (
          <button
            key={animation.id}
            type="button"
            onClick={() => handleSelectAnimation(animation.id)}
            className={`flex flex-col items-center justify-center gap-2 py-4 border rounded-lg cursor-pointer ${
              config?.type === animation.id ? 'border-[#FFCC00] bg-[#FFF9E5]' : 'border-gray-200 bg-white'
            }`}
          >
            <AnimationIcon type={animation.id} />
            <span className="text-sm font-medium text-gray-900">{animation.label}</span>
          </button>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold text-gray-900">Duración</h4>
          <Input
            id="canvas-animation-duration"
            name="canvas-animation-duration"
            value={customDuration}
            onChange={(e) => handleDurationChange(e.target.value)}
            className="h-8 text-sm text-center text-gray-600 [--form-field-container-width:4rem] [--form-field-padding-y:0.35rem] [--form-field-padding-x:0.4rem] [--form-field-default-border:1px_solid_#e5e7eb] [--form-field-default-bg:white]"
          />
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {DURATION_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handleDurationChange(String(preset))}
              className={`h-9 rounded-md border text-sm font-medium cursor-pointer ${
                Math.abs(Number.parseFloat(customDuration) - preset) < 0.01
                  ? 'border-[#FFCC00] bg-[#FFF9E5] text-gray-900'
                  : 'border-gray-200 bg-white text-gray-600'
              }`}
            >
              {preset}s
            </button>
          ))}
        </div>
        <input
          type="range"
          min={0.1}
          max={3}
          step={0.1}
          value={customDuration}
          onChange={(e) => handleDurationChange(e.target.value)}
          className="w-full accent-[#FFCC00] cursor-pointer"
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-gray-900">Loop infinito</span>
        <Toggle
          id="canvas-animation-loop"
          name="canvas-animation-loop"
          checked={config?.loopInfinite ?? false}
          onChange={(e) => handleLoopInfiniteChange(e.target.checked)}
        />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-gray-900">Número de repeticiones</h4>
          <Input
            id="canvas-animation-repeat-count"
            name="canvas-animation-repeat-count"
            value={customRepeatCount}
            onChange={(e) => handleRepeatCountChange(e.target.value)}
            disabled={config?.loopInfinite}
            className="h-8 text-sm text-gray-600 [--form-field-container-width:5rem] [--form-field-padding-y:0.35rem] [--form-field-padding-x:0.5rem] [--form-field-default-border:1px_solid_#e5e7eb] [--form-field-default-bg:white]"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-bold text-gray-900">Delay</h4>
            <p className="text-xs text-gray-500">En segundos</p>
          </div>
          <Input
            id="canvas-animation-delay"
            name="canvas-animation-delay"
            value={customDelay}
            onChange={(e) => handleDelayChange(e.target.value)}
            className="h-8 text-sm text-gray-600 [--form-field-container-width:5rem] [--form-field-padding-y:0.35rem] [--form-field-padding-x:0.5rem] [--form-field-default-border:1px_solid_#e5e7eb] [--form-field-default-bg:white]"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={removeAnimation}
        disabled={!config?.type}
        className={`h-10 rounded-md text-sm font-semibold ${
          config?.type ? 'bg-gray-100 text-gray-700 cursor-pointer hover:bg-gray-200' : 'bg-gray-50 text-gray-300 cursor-not-allowed'
        }`}
      >
        Eliminar animación
      </button>
    </div>
  );
};

export default CanvasAnimationTab;
